import type { GameAction } from "@fate-kingdom/engine";
import { create } from "zustand";

import type { WebGlContextStatus } from "../battlefield-3d/ContextLifecycle";
import {
  ALPHA_DEFAULT_SEED,
  createAlphaSession,
  type AlphaMatchConfig,
  type AlphaSessionController,
  type AlphaSessionSnapshot,
} from "./alpha-session-controller";
import type { AlphaPresentationTransaction } from "./presentation/presentation-transaction";

export const DEFAULT_ALPHA_MATCH_CONFIG: AlphaMatchConfig = {
  humanPlayerId: "player-1",
  humanDeckId: "assault",
  aiDeckId: "balanced",
  seed: ALPHA_DEFAULT_SEED,
};

interface AlphaGameStoreState {
  readonly controller: AlphaSessionController | null;
  readonly snapshot: AlphaSessionSnapshot | null;
  readonly sessionToken: number;
  readonly paused: boolean;
  readonly quickAi: boolean;
  readonly contextStatus: WebGlContextStatus;
  readonly validationMessage: string | null;
  readonly presentationQueue: readonly AlphaPresentationTransaction[];
  start: (config: AlphaMatchConfig) => void;
  leave: () => void;
  applyHumanAction: (action: GameAction) => void;
  stepAi: (sessionToken: number) => void;
  acknowledgePresentation: (actionSequence: number) => void;
  setPaused: (paused: boolean) => void;
  setQuickAi: (quick: boolean) => void;
  setContextStatus: (status: WebGlContextStatus) => void;
  clearValidationMessage: () => void;
}

function resultMessage(
  result: ReturnType<AlphaSessionController["applyHumanAction"]>,
): string | null {
  return result.ok ? null : result.failure.message;
}

export const useAlphaGameStore = create<AlphaGameStoreState>((set, get) => ({
  controller: null,
  snapshot: null,
  sessionToken: 0,
  paused: false,
  quickAi: false,
  contextStatus: "initializing",
  validationMessage: null,
  presentationQueue: [],
  start: (config) => {
    const created = createAlphaSession(config);
    const sessionToken = get().sessionToken + 1;
    if (!created.ok) {
      set({
        controller: null,
        snapshot: null,
        sessionToken,
        paused: true,
        quickAi: false,
        validationMessage: created.failure.message,
        presentationQueue: [],
      });
      return;
    }
    set({
      controller: created.controller,
      snapshot: created.controller.snapshot,
      sessionToken,
      paused: false,
      quickAi: false,
      validationMessage: null,
      presentationQueue: [],
    });
  },
  leave: () => {
    set((state) => ({
      controller: null,
      snapshot: null,
      sessionToken: state.sessionToken + 1,
      paused: false,
      quickAi: false,
      validationMessage: null,
      presentationQueue: [],
    }));
  },
  applyHumanAction: (action) => {
    const state = get();
    const controller = state.controller;
    if (controller === null || state.presentationQueue.length > 0) return;
    const result = controller.applyHumanAction(action);
    set({
      snapshot: controller.snapshot,
      sessionToken: state.sessionToken + 1,
      validationMessage: resultMessage(result),
      quickAi: false,
      presentationQueue: result.ok ? [...state.presentationQueue, result.transaction] : [],
    });
  },
  stepAi: (sessionToken) => {
    const state = get();
    if (
      state.controller === null ||
      state.sessionToken !== sessionToken ||
      state.paused ||
      state.presentationQueue.length > 0 ||
      state.contextStatus === "lost"
    ) {
      return;
    }
    const result = state.controller.stepAi();
    const nextSnapshot = state.controller.snapshot;
    const keepQuickAi =
      result.ok &&
      state.quickAi &&
      state.controller.isAiTurn &&
      !state.controller.isFinished &&
      nextSnapshot.failure === null;
    set({
      snapshot: nextSnapshot,
      sessionToken: state.sessionToken + 1,
      validationMessage: resultMessage(result),
      quickAi: keepQuickAi,
      presentationQueue: result.ok ? [...state.presentationQueue, result.transaction] : [],
    });
  },
  acknowledgePresentation: (actionSequence) => {
    set((state) => {
      if (state.presentationQueue[0]?.actionSequence !== actionSequence) return state;
      return {
        presentationQueue: state.presentationQueue.slice(1),
        sessionToken: state.sessionToken + 1,
      };
    });
  },
  setPaused: (paused) => set({ paused, quickAi: false }),
  setQuickAi: (quickAi) => {
    const state = get();
    const eligible =
      quickAi &&
      state.controller !== null &&
      state.controller.isAiTurn &&
      !state.controller.isFinished &&
      state.snapshot?.failure === null &&
      state.presentationQueue.length === 0 &&
      state.contextStatus !== "lost";
    set({ quickAi: eligible, paused: eligible ? false : state.paused });
  },
  setContextStatus: (contextStatus) => {
    set((state) => ({
      contextStatus,
      sessionToken: contextStatus === "lost" ? state.sessionToken + 1 : state.sessionToken,
      paused: contextStatus === "lost" ? true : state.paused,
      quickAi: contextStatus === "lost" ? false : state.quickAi,
      validationMessage:
        contextStatus === "lost"
          ? "WebGL paused. The deterministic match state is safe; resume after the scene restores."
          : state.validationMessage,
      presentationQueue: contextStatus === "lost" ? [] : state.presentationQueue,
    }));
  },
  clearValidationMessage: () => set({ validationMessage: null }),
}));
